<template>
  <LazyHomeCommonLayout>
    <template #content>
      <div class="subPage">
        <div class="backBg"></div>
        <div class="checkoutWrap flex col">
          <section id="checkout">
            <div class="container_main">
              <div v-if="!isLoading && payment" class="checkout-result flex f-center">
                <section class="checkout-result__card flex col item-center gap-24">

                  <header class="checkout-result__header flex col item-center gap-24">
                    <div class="checkout-result__icon flex f-center" aria-hidden="true">
                      <img src="~/assets/images/ic_login_check_2x.png" alt="complete icon" style="width: 40px">
                    </div>
                    <h1 class="checkout-result__title">
                      {{ planName }}<br>{{ t('payment-complete-title') }}
                    </h1>
                  </header>

                  <div class="checkout-details">
                    <div class="checkout-details__header flex justify-between gap-10">
                      <h2 class="checkout-details__title">{{ t('payment-complete-payment-history') }}</h2>
                      <!-- PAY-PROC-09: PG 제공 청구서(receiptUrl)를 새 창(_blank)으로 연다. -->
                      <a v-if="payment.receiptUrl" :href="payment.receiptUrl" target="_blank" rel="noopener noreferrer" class="btn secondaryBtn2">
                        {{ t('payment-complete-view-invoice') }}
                      </a>
                    </div>

                    <dl class="checkout-details__list flex col gap-12">
                      <div class="checkout-details__item">
                        <dt>{{ t('payment-complete-payment-date') }}</dt>
                        <dd>{{ paymentDate }}</dd>
                      </div>
                      <div class="checkout-details__item">
                        <dt>{{ t('payment-complete-payment-amount') }}</dt>
                        <dd>{{ amount }}</dd>
                      </div>
                      <div class="checkout-details__item">
                        <dt>{{ t('payment-complete-next-billing-date') }}</dt>
                        <dd>{{ nextBillingDate }}</dd>
                      </div>
                    </dl>

                    <div class="checkout-details__notice p-box">
                      <ul class="listDot">
                        <li>
                          <span v-html="paymentCompleteDescriptionDom" />
                        </li>
                      </ul>
                    </div>
                  </div>

                  <footer class="checkout-result__actions">
                    <NuxtLink to="/settings/org?tab=PAYMENT" class="btn primaryBtn">
                      {{ t('payment-complete-button') }}
                    </NuxtLink>
                  </footer>

                </section>
              </div>
            </div>
          </section>
        </div>
      </div>
    </template>
  </LazyHomeCommonLayout>
</template>

<script setup lang="ts">
import {useI18n} from 'vue-i18n';
import {getTimeZoneDateString} from '~/utils/CovertDateUtill';

useHead({
  title: 'Subscription Complete | Codepresso',
  meta: [{ name: 'robots', content: 'noindex,nofollow' }],
})

definePageMeta({
  ssr: false,
  alias: [
    '/en/home/pricing/checkout/complete',
    '/ja/home/pricing/checkout/complete',
    '/ko/home/pricing/checkout/complete',
    '/vi/home/pricing/checkout/complete',
  ],
})

import { SignType, SignUIType } from "~/components/Sign/enum/SignType";
import { useUserStore } from '~/store/user/userStore'
import { CookieKeyType } from '~~/package/mapper/common/model/CookieKeyType'
import { useContainer } from '~/composables/useContainer'
import { DependencyInjectId } from '~~/package/const/DependencyInjectId'
import type SubscriptionApi from '~~/package/apis/subscription/SubscriptionApi'
import type PaymentResponse from '~~/package/apis/subscription/response/PaymentResponse'
import {computed} from 'vue';

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const { notifySubscriptionUpdate } = useSubscriptionBroadcast();
const subscriptionApi = useContainer<SubscriptionApi>(DependencyInjectId.SubscriptionApi)

const payment = ref<PaymentResponse | null>(null)
const isLoading = ref(true)

onMounted(async () => {
  notifySubscriptionUpdate();

  if (!userStore.isAuthResolved) {
    await new Promise<void>((resolve) => {
      const stop = watch(() => userStore.isAuthResolved, (resolved) => {
        if (resolved) { stop(); resolve(); }
      });
    });
  }

  if (userStore.isLogOut()) {
    setCookie(CookieKeyType.REDIRECT_URL, {
        fullPath: route.fullPath,
        path: route.path,
        query: route.query,
        params: route.params,
        isManager: false
      }, {
        expires: new Date(Date.now() + 86400 * 1000),
        maxAge: 86400
      });
      router.replace({ path: '/sign', query: { uiType: SignUIType.LOGIN, type: SignType.CANDIDATE } });
      return;
  }

  const paymentKey = route.query.paymentKey as string
  if (!paymentKey) {
    router.replace('/404')
    return
  }

  try {
    const response = await subscriptionApi.getPayment(paymentKey)
    payment.value = response.data
  } catch {
    router.replace('/404')
    return
  } finally {
    isLoading.value = false
  }
})

const paymentCompleteDescriptionDom = computed(() => {
  return t('payment-complete-description', {
    payment: `<a href="/settings/org?tab=PAYMENT&view=history" class="linkTxt">${t('payment-complete-payment-history')}</a>`
  })
})

const planName = computed(() => {
  const planTier = payment.value?.planTier?.toLowerCase() ?? ''
  const billingCycle = payment.value?.billingCycle?.toLowerCase() ?? ''
  return `${t(`pricing-${planTier}`)} - ${t(`pricing-${billingCycle}`)}`
})

const amount = computed(() => {
  if (!payment.value) return ''
  const symbol = payment.value.currency === 'KRW' ? '₩' : '$'
  return `${symbol}${payment.value.finalAmount.toLocaleString()}`
})

const formatDate = (dateStr: string | undefined | null, timeZoneId = ''): string => {
  if (!dateStr) return '-'
  const localDateStr = getTimeZoneDateString(dateStr, timeZoneId)
  return new Date(localDateStr).toLocaleDateString(locale.value, { year: 'numeric', month: 'long', day: 'numeric' })
}

// PAY-PROC-09: 결제일·다음 결제일은 결제 시점 timezone 기준 날짜로 고정 표시.
const paymentDate = computed(() => formatDate(payment.value?.paidAt, payment.value?.timeZoneId))
const nextBillingDate = computed(() => formatDate(payment.value?.nextBillingDate, payment.value?.timeZoneId))
</script>

<style>
@import "~/assets/scss/main_style.scss";
</style>
